/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OrderLineDeliveryScheduleData {
    SalesDocumentLineNumber: string;
    DeliveryScheduleLineNumber: string;
    ScheduleLineDate: string;
    QtyOrdered: number;
    ConfirmedQty: number;
    CorrectedQty: number;
    SalesUnit: string;
    Suppress: boolean;
}

interface OwnProps {
    deliveryScheduleData: OrderLineDeliveryScheduleData[];
    unitOfMeasure?: string;
}

export interface OrderDetailsDeliveryScheduleProps {
    tableRowStyle?: DataTableRowProps;
    tableCellStyle?: DataTableCellProps;
    hiddenHeaderStyle?: HiddenProps;
    tableCellGridStyle?: GridItemProps;
    deliveryScheduleTableHeaderStyle?: DataTableHeadProps;
    tableHeaderStyle?: DataTableHeaderProps;
}

export const orderDetailsDeliveryScheduleStyles: OrderDetailsDeliveryScheduleProps = {};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = orderDetailsDeliveryScheduleStyles;

const OrderDetailsDeliverySchedule: FC<Props> = ({ deliveryScheduleData, unitOfMeasure, language }) => {
    const headerColumns = [
        `${translate("Est. Delivery Date")}`,
        `${translate("Qty. Confirmed")}`,
        `${translate("UOM")}`,
    ];

    return (
        <DataTable
            css={css`
                width: 100%;
            `}
        >
            <DataTableHead {...styles.deliveryScheduleTableHeaderStyle}>
                {headerColumns.map((column, index) => (
                    <DataTableHeader key={`${column}_${index}`} {...styles.tableHeaderStyle}>
                        {column}
                    </DataTableHeader>
                ))}
            </DataTableHead>
            <DataTableBody>
                {deliveryScheduleData
                    .filter(item => !item.Suppress)
                    .map((item, index) => {
                        return (
                            <DataTableRow key={`delivery-schedule-${index}`} {...styles.tableRowStyle}>
                                <DataTableCell {...styles.tableCellStyle}>
                                    <Hidden {...styles.hiddenHeaderStyle}>
                                        <GridItem {...styles.tableCellGridStyle}>{headerColumns[0]}</GridItem>
                                    </Hidden>
                                    <GridItem {...styles.tableCellGridStyle}>
                                        {getLocalizedDateTime({
                                            dateTime: new Date(item.ScheduleLineDate),
                                            language,
                                            options: {
                                                day: "numeric",
                                                month: "numeric",
                                                year: "numeric",
                                                timeZone: "UTC",
                                            },
                                        })}
                                    </GridItem>
                                </DataTableCell>
                                <DataTableCell {...styles.tableCellStyle}>
                                    <Hidden {...styles.hiddenHeaderStyle}>
                                        <GridItem {...styles.tableCellGridStyle}>{headerColumns[2]}</GridItem>
                                    </Hidden>
                                    <GridItem {...styles.tableCellGridStyle}>{item.ConfirmedQty}</GridItem>
                                </DataTableCell>
                                <DataTableCell {...styles.tableCellStyle}>
                                    <Hidden {...styles.hiddenHeaderStyle}>
                                        <GridItem {...styles.tableCellGridStyle}>{headerColumns[4]}</GridItem>
                                    </Hidden>
                                    <GridItem {...styles.tableCellGridStyle}>
                                        {item.SalesUnit || unitOfMeasure || "– –"}
                                    </GridItem>
                                </DataTableCell>
                            </DataTableRow>
                        );
                    })}
            </DataTableBody>
        </DataTable>
    );
};

export default connect(mapStateToProps)(OrderDetailsDeliverySchedule);
